/**
 * Style Remover Utilities
 * Handles removal of Microsoft Office specific styles and classes from HTML
 */

/**
 * Removes all mso-* (Microsoft Office) CSS properties from style attributes
 * @param html - HTML string to clean
 * @returns HTML with mso-* properties removed
 */
export function removeMsoStyles(html: string): string {
  // Remove mso-* properties from style attributes
  // Matches: mso-property-name: value; or mso-property-name: value (at end)
  html = html.replace(
    /style="([^"]*)"/gi,
    (match, styleContent: string) => {
      const cleanedStyle = styleContent
        // Remove mso-* properties
        .replace(/mso-[^:]+:[^;]+;?/gi, "")
        // Remove tab-stops property (Word specific)
        .replace(/tab-stops:[^;]+;?/gi, "")
        // Remove font-family declarations with Word-specific fonts
        .replace(/font-family:[^;]*(?:Calibri|Times New Roman|Arial)[^;]*;?/gi, "")
        // Clean up multiple semicolons
        .replace(/;+/g, ";")
        // Clean up leading/trailing semicolons and whitespace
        .replace(/^\s*;|;\s*$/g, "")
        .trim();

      return cleanedStyle ? `style="${cleanedStyle}"` : "";
    }
  );

  // Remove empty style attributes that may remain
  html = html.replace(/\s*style="\s*"/gi, "");

  return html;
}

/**
 * Removes Microsoft Office specific CSS classes (Mso*) from class attributes
 * @param html - HTML string to clean
 * @returns HTML with Mso* classes removed
 */
export function removeMsoClasses(html: string): string {
  html = html.replace(
    /class="([^"]*)"/gi,
    (match, classContent: string) => {
      // Split classes, filter out Mso* classes, rejoin
      const nonMsoClasses = classContent
        .split(/\s+/)
        .filter((cls) => !cls.startsWith("Mso") && cls.trim() !== "")
        .join(" ")
        .trim();

      return nonMsoClasses ? `class="${nonMsoClasses}"` : "";
    }
  );

  // Remove empty class attributes
  html = html.replace(/\s*class="\s*"/gi, "");

  return html;
}

/**
 * Removes Word-specific XML namespace declarations
 * @param html - HTML string to clean
 * @returns HTML with namespace declarations removed
 */
export function removeNamespaceDeclarations(html: string): string {
  // Remove xmlns declarations for Office namespaces
  html = html.replace(/\s*xmlns:[a-z]+=["'][^"']*["']/gi, "");

  return html;
}

/**
 * Removes Office-specific elements like <o:p>, <w:sdt>, etc.
 * @param html - HTML string to clean
 * @returns HTML with Office elements removed
 */
export function removeOfficeElements(html: string): string {
  // Remove <o:p> elements (Office paragraph markers)
  html = html.replace(/<o:p[^>]*>[\s\S]*?<\/o:p>/gi, "");
  html = html.replace(/<o:p[^>]*\/>/gi, "");

  // Remove other Office namespace elements
  html = html.replace(/<[a-z]+:[^>]*>[\s\S]*?<\/[a-z]+:[^>]*>/gi, "");
  html = html.replace(/<[a-z]+:[^>]*\/>/gi, "");

  return html;
}

/**
 * Removes Word conditional comments
 * @param html - HTML string to clean
 * @returns HTML with conditional comments removed
 */
export function removeConditionalComments(html: string): string {
  // Remove <!--[if ...]>...<![endif]--> blocks
  html = html.replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, "");

  // Remove regular HTML comments
  html = html.replace(/<!--[\s\S]*?-->/g, "");

  return html;
}

/**
 * Removes specific inline styles that shouldn't be preserved
 * @param html - HTML string to clean
 * @returns HTML with unwanted inline styles removed
 */
export function removeUnwantedInlineStyles(html: string): string {
  html = html.replace(
    /style="([^"]*)"/gi,
    (match, styleContent: string) => {
      const cleanedStyle = styleContent
        // Remove line-height with specific pt values (Word default)
        .replace(/line-height:\s*[\d.]+pt;?/gi, "")
        // Remove margin-bottom with tiny values (Word artifact)
        .replace(/margin-bottom:\s*\.0001pt;?/gi, "")
        // Remove text-indent: 0 (unnecessary)
        .replace(/text-indent:\s*0[^;]*;?/gi, "")
        // Remove background: white/transparent (unnecessary)
        .replace(/background:\s*(?:white|transparent|#fff(?:fff)?);?/gi, "")
        // Clean up
        .replace(/;+/g, ";")
        .replace(/^\s*;|;\s*$/g, "")
        .trim();

      return cleanedStyle ? `style="${cleanedStyle}"` : "";
    }
  );

  html = html.replace(/\s*style="\s*"/gi, "");

  return html;
}

/**
 * Comprehensive style cleanup - runs all style removal functions
 * @param html - HTML string to clean
 * @returns Fully cleaned HTML
 */
export function removeAllMsoArtifacts(html: string): string {
  html = removeConditionalComments(html);
  html = removeOfficeElements(html);
  html = removeNamespaceDeclarations(html);
  html = removeMsoStyles(html);
  html = removeMsoClasses(html);
  html = removeUnwantedInlineStyles(html);

  return html;
}
